'use client'

import { createContext, useContext, useEffect, useState } from 'react'
import dynamic from 'next/dynamic'
import { ArrowLeft, Check, CircleDot, Crown, Dices, Lock, Palette, Store, X } from 'lucide-react'
import { CoinIcon } from '@/components/coin-icon'
import { GamesHubBar } from '@/components/hub'
import { HOME_THEMES, getHomeTheme, homeThemeVars, resolveHomeThemeId, type HomeTheme } from '@/lib/home-themes'

// Games list (opened from the game-pad button in the top bar). Add a new game here: one entry in
// GAMES + one `onOpen` case in app/page.tsx. Ludo, Chess and Carrom are live; anything else shows as "Soon".
//
// THEMES: the Games list has its own theme, picked with the palette button (same catalogue as the home
// page: lib/home-themes.ts - free themes for everyone, premium ones with the same unlock as the home page).
// The choice is saved on the profile as `gamesTheme` (id, or '' = the classic black look).

const PrizeStore = dynamic(() => import('@/components/prizes').then((m) => m.PrizeStore), { ssr: false })

type Game = { id: string; name: string; tag: string; chips: string[]; icon: React.ReactNode; grad: string; live: boolean; img?: string }

const GAMES: Game[] = [
  { img: '/games/ludo.webp', id: 'ludo', name: 'Ludo', tag: '1v1, 3 or 4 players · Online + Offline', chips: ['1v1', '3 or 4 players', 'Online', 'Offline'], icon: <Dices size={42} />, grad: 'linear-gradient(150deg, #f97316, #ea580c)', live: true },
  { img: '/games/chess.webp', id: 'chess', name: 'Chess', tag: 'Real chess · clocks · Online + Offline', chips: ['Real chess', 'Clocks', 'Online', 'Offline'], icon: <Crown size={42} />, grad: 'linear-gradient(150deg, #64748b, #475569)', live: true },
  { img: '/games/carrom.webp', id: 'carrom', name: 'Carrom', tag: '1v1 or 2v2 · Flick, pocket, win · Online + Offline', chips: ['1v1', '2v2 teams', 'Online', 'Offline'], icon: <CircleDot size={42} />, grad: 'linear-gradient(150deg, #d99a4e, #8a5528)', live: true },
]

/** The active HOME theme. Games list + Ludo / Chess lobbies follow it, so changing the home theme changes them too. */
export const GameThemeContext = createContext<HomeTheme | null>(null)
/** className + inline style for a lobby / list root so it wears the home theme (null theme = classic look). */
export function useGameTheme(): { cls: string; style: React.CSSProperties | undefined } {
  const t = useContext(GameThemeContext)
  if (!t) return { cls: '', style: undefined }
  return {
    cls: `gm-themed ${t.dark ? 'gm-dark' : 'gm-light'}`,
    style: {
      ...homeThemeVars(t), ['--gm-a' as any]: t.a, ['--gm-b' as any]: t.b,
      backgroundColor: t.bg, backgroundImage: 'var(--home-pattern)', backgroundSize: 'var(--home-pattern-size)',
      backgroundRepeat: 'var(--home-pattern-repeat)', backgroundPosition: 'var(--home-pattern-position)',
    } as React.CSSProperties,
  }
}

/** The theme that is really in effect: a premium theme falls back to the classic look while it is locked. */
export const resolveGamesTheme = (id: string | undefined, premiumUnlocked: boolean): HomeTheme | undefined => {
  const t = getHomeTheme(id)
  return t && (!t.premium || premiumUnlocked) ? t : undefined
}

/** Coin counter that counts up from 0 when the page opens (and eases to any later change). */
function useCountUp(target: number, ms = 900) {
  const [v, setV] = useState(0)
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) { setV(target); return }
    let raf = 0; const from = 0; const t0 = performance.now()
    const tick = (t: number) => {
      const k = Math.min(1, (t - t0) / ms)
      setV(Math.round(from + (target - from) * (1 - Math.pow(1 - k, 3))))
      if (k < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target, ms])
  return v
}

export function GamesPage({ coinBalance, onClose, onOpen, onOpenWallet, theme, premiumUnlocked, onPickTheme, onGetPremium, streak = 0, friends = [], onViewProfile, onCoins }: {
  streak?: number; friends?: { id: string; name: string; avatar: string }[]; onViewProfile?: (uid: string) => void; onCoins?: (n: number, label: string) => void
  coinBalance: number; onClose: () => void; onOpen: (id: string) => void; onOpenWallet: () => void
  /** saved theme id ('' / unknown = classic black look) */
  theme?: string
  /** true when premium themes are unlocked for this account */
  premiumUnlocked?: boolean
  onPickTheme?: (id: string) => void
  onGetPremium?: () => void
}) {
  const [picker, setPicker] = useState(false)
  const [prizes, setPrizes] = useState(false)
  const shownCoins = useCountUp(coinBalance)
  const gt = useGameTheme() // follows the home theme (the separate games theme is no longer used)
  return (
    <main className={`lu-root au-shell gm-root ${gt.cls}`} style={gt.style}>
      <div className="au-card lu-card gm-card">
        <div className="lu-topbar">
          <button type="button" className="lu-icon-btn" onClick={onClose} aria-label="Back"><ArrowLeft size={18} /></button>
          <div className="gm-top-right">
            <button type="button" className="lu-icon-btn gm-store-btn" onClick={() => setPrizes(true)} aria-label="Store" title="Store"><Store size={17} /><span>Store</span></button>
            {onPickTheme && <button type="button" className="lu-icon-btn" onClick={() => setPicker(true)} aria-label="Change games theme" title="Change theme"><Palette size={17} /></button>}
            <button type="button" className="coin-chip" onClick={onOpenWallet} aria-label="Coin wallet"><CoinIcon size={18} /><span>{shownCoins.toLocaleString('en-IN')}</span></button>
          </div>
        </div>
        <h1 className="au-title">Games</h1>
        <p className="au-sub">Play online with coins, or offline against bots.</p>
        <GamesHubBar streak={streak} friends={friends} onViewProfile={onViewProfile} onCoins={onCoins} />
        <ul className="gm-list">
          {GAMES.map((g, i) => (
            <li key={g.id} style={{ ['--i' as any]: i } as React.CSSProperties}>
              <button type="button" className={`gm-item gm-hero gm-g-${g.id} ${g.live ? '' : 'soon'}`} disabled={!g.live} onClick={() => onOpen(g.id)}>
                {g.img
                  ? <span className="gm-ico gm-ico-xl gm-ico-pic"><img src={g.img} alt="" draggable={false} width={76} height={76} /></span>
                  : <span className="gm-ico gm-ico-xl" style={{ background: g.grad }}><span className="gm-ico-in">{g.icon}</span></span>}
                <span className="gm-txt">
                  <strong>{g.name}</strong>
                  <span className="gm-chips" aria-label={g.tag}>{g.chips.map((c) => <em key={c} className="gm-chip">{c}</em>)}</span>
                </span>
                {g.live ? <span className="gm-play">Play</span> : <span className="gm-soon"><Lock size={12} /> Soon</span>}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {prizes && <PrizeStore coinBalance={coinBalance} onClose={() => setPrizes(false)} />}

      {picker && onPickTheme && (
        <GamesThemePicker
          current="" premiumUnlocked={!!premiumUnlocked}
          onPick={(id) => { onPickTheme(id); setPicker(false) }}
          onGetPremium={() => { setPicker(false); onGetPremium?.() }}
          onClose={() => setPicker(false)}
        />
      )}
    </main>
  )
}

function GamesThemePicker({ current, premiumUnlocked, onPick, onGetPremium, onClose }: {
  current: string; premiumUnlocked: boolean; onPick: (id: string) => void; onGetPremium: () => void; onClose: () => void
}) {
  const tile = (t: HomeTheme) => {
    const locked = t.premium && !premiumUnlocked
    const on = resolveHomeThemeId(current) === t.id
    return (
      <button key={t.id} type="button" className={`gm-tile ${on ? 'on' : ''}`} onClick={() => (locked ? onGetPremium() : onPick(t.id))} aria-pressed={on} aria-label={`${t.label}${locked ? ' (premium)' : ''}`}>
        <span className="gm-tile-sw" style={{ backgroundColor: t.bg, backgroundImage: `url(${t.thumb ?? t.url})`, backgroundSize: t.cover ? 'cover' : '80px 80px', backgroundRepeat: t.cover ? 'no-repeat' : 'repeat', backgroundPosition: 'center' }}>
          <i className="gm-tile-card" style={{ background: t.paper, borderColor: t.line }} />
          <i className="gm-tile-dot" style={{ background: t.a }} />
          {locked && <b className="gm-tile-badge lock"><Lock size={11} /></b>}
          {on && <b className="gm-tile-badge ok"><Check size={11} strokeWidth={3.5} /></b>}
        </span>
        <small>{t.label}</small>
      </button>
    )
  }
  return (
    <div className="gm-sheet-bg" onClick={onClose} role="dialog" aria-modal="true" aria-label="Games theme">
      <div className="gm-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="gm-sheet-head">
          <strong><Palette size={17} /> Games theme</strong>
          <button type="button" className="lu-icon-btn" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>
        <div className="gm-sheet-h">Classic</div>
        <div className="gm-tiles">
          <button type="button" className={`gm-tile ${current === '' ? 'on' : ''}`} onClick={() => onPick('')} aria-pressed={current === ''}>
            <span className="gm-tile-sw" style={{ background: 'radial-gradient(90% 60% at 12% 0%, rgba(255,61,120,.35), transparent 62%), #000' }}>
              <i className="gm-tile-card" style={{ background: '#111118', borderColor: '#2a2a35' }} />
              <i className="gm-tile-dot" style={{ background: '#ff4d6d' }} />
              {current === '' && <b className="gm-tile-badge ok"><Check size={11} strokeWidth={3.5} /></b>}
            </span>
            <small>Black (default)</small>
          </button>
        </div>
        <div className="gm-sheet-h">Free</div>
        <div className="gm-tiles">{HOME_THEMES.filter((t) => !t.premium).map(tile)}</div>
        <div className="gm-sheet-h"><Crown size={13} /> Premium {!premiumUnlocked && <em>- tap to unlock</em>}</div>
        <div className="gm-tiles">{HOME_THEMES.filter((t) => t.premium).map(tile)}</div>
      </div>
    </div>
  )
}
